import { useEditorStore } from "@/store";
import { InfoBanner } from "@/components/layout/InfoBanner";
import { StringField } from "@/components/fields/StringField";
import { SectionHeader } from "@/components/fields/SectionHeader";
import { GLYPH_PRESETS, SPRITE_PRESETS } from "@/lib/presets";

export function AssetsSection() {
  const style = useEditorStore((s) => s.style);
  const updateRoot = useEditorStore((s) => s.updateRoot);

  const sprite = typeof style.sprite === "string" ? style.sprite : "";
  const glyphs = typeof style.glyphs === "string" ? style.glyphs : "";

  return (
    <div>
      <InfoBanner storageKey="section-assets">
        External resources the map needs to render. Sprites provide icons and
        patterns for symbol and fill layers. Glyphs provide fonts for text labels.
        Without a glyphs URL, text labels won't render.
      </InfoBanner>

      {/* Sprite */}
      <SectionHeader title="Sprite" description="Icon and pattern images used by symbol and fill layers">
        <StringField
          label="Sprite URL"
          value={sprite}
          onChange={(v) => updateRoot("sprite", v || undefined)}
          placeholder="https://example.com/sprites/style"
        />
        <PresetPicker
          items={SPRITE_PRESETS}
          currentValue={sprite}
          onSelect={(p) => updateRoot("sprite", p.url)}
          renderLabel={(p) => p.name}
          renderDetail={(p) => p.description}
        />
      </SectionHeader>

      {/* Glyphs */}
      <SectionHeader title="Glyphs" description="Font rendering for text labels (SDF glyphs)">
        <StringField
          label="Glyphs URL"
          value={glyphs}
          onChange={(v) => updateRoot("glyphs", v || undefined)}
          placeholder="https://example.com/fonts/{fontstack}/{range}.pbf"
        />
        <PresetPicker
          items={GLYPH_PRESETS}
          currentValue={glyphs}
          onSelect={(p) => updateRoot("glyphs", p.url)}
          renderLabel={(p) => p.name}
          renderDetail={(p) => (
            <>
              <div>{p.description}</div>
              <div className="mt-0.5 font-mono text-[8px]">{p.fonts.slice(0, 4).join(", ")}{p.fonts.length > 4 ? ` +${p.fonts.length - 4} more` : ""}</div>
            </>
          )}
        />
      </SectionHeader>

      {/* Metadata */}
      <SectionHeader title="Metadata" description="Custom style metadata (read-only)">
        <div className="rounded-radius-sm bg-surface-sunken p-2 font-mono text-[11px] text-text-secondary">
          <pre className="overflow-x-auto whitespace-pre-wrap">
            {JSON.stringify(style.metadata ?? {}, null, 2)}
          </pre>
        </div>
      </SectionHeader>
    </div>
  );
}

// ── Reusable preset picker ──

function PresetPicker<T extends { url: string }>({
  items,
  currentValue,
  onSelect,
  renderLabel,
  renderDetail,
}: {
  items: T[];
  currentValue: string;
  onSelect: (item: T) => void;
  renderLabel: (item: T) => string;
  renderDetail: (item: T) => React.ReactNode;
}) {
  return (
    <div className="mt-2 space-y-0.5">
      <div className="mb-1 text-[9px] font-medium text-text-muted">Presets</div>
      {items.map((item) => (
        <button
          key={item.url}
          type="button"
          onClick={() => onSelect(item)}
          className={`flex w-full items-start gap-2 rounded-radius-sm px-2 py-1.5 text-left transition-colors ${
            currentValue === item.url
              ? "bg-accent-muted ring-1 ring-accent/30"
              : "hover:bg-surface-sunken"
          }`}
        >
          <div className="flex-1">
            <div className="text-[10px] font-medium text-text-primary">{renderLabel(item)}</div>
            <div className="text-[9px] text-text-muted">{renderDetail(item)}</div>
          </div>
          {currentValue === item.url && (
            <span className="shrink-0 text-[9px] text-accent">✓</span>
          )}
        </button>
      ))}
    </div>
  );
}
